/**
 * The full-page countdown — the pilot's `.cdfull`, rebuilt.
 *
 * Ticks from the device clock and needs no network, which is the point: it is
 * checked in a field with no signal, and on the train the week before.
 *
 * ---
 *
 * **What it counts to, and why it stopped counting.**
 *
 * It counts to THIS edition's gates and nothing else. From step 51 to
 * 2026-09-28 it rolled over to the next edition once this one ended — the
 * pilot's habit — and the user had it removed: each year is its own rave with
 * its own card, and a finished rave counting down to another rave's gates is
 * the wrong rave's countdown. A finished rave says it has ended.
 *
 * **The artwork is the edition's own cover**, under the pilot's four-stop
 * veil: light at the top so the photograph reads behind the logo, heavy at
 * the bottom so the digits and the dates sit on something close to plain
 * ground.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { useEdition } from '@/app/EditionProvider';
import { useLocale } from '@/app/LocaleProvider';
import { Stack, localDateRange } from '@/ui';
import { Logo } from './Logo';
import { countdown, formatDateRange, toDisplayTemp, type RaveWeather } from '@myrave/shared';
import { useT } from '@/i18n';
import { useRaveWeather } from './raveWeather';
import './Countdown.css';

/**
 * Inside a rave: read the edition and hand it to the panel.
 *
 * The panel below takes plain values on purpose. The directory's cards open
 * the same countdown from `app.`, where there is no bundle and never will be
 * — downloading a festival's entire lineup to render four numbers is the
 * thing `/me/festivals` exists to avoid.
 */
export function Countdown({ onClose }: { onClose: () => void }) {
  const { bundle, timezone } = useEdition();
  const tr = useT();
  const { weather } = useRaveWeather(bundle.edition.id, bundle);
  return (
    <CountdownPanel
      onClose={onClose}
      name={bundle.edition.name}
      venue={bundle.edition.venue}
      gatesOpenAt={bundle.edition.gatesOpenAt}
      endsAt={bundle.edition.endsAt}
      coverUrl={bundle.edition.coverUrl}
      logo={<Logo height={54} />}
      timezone={timezone}
      weatherLine={outlook(weather, tr)}
    />
  );
}

/**
 * The rave's days at a glance, for the countdown (step 65): the warmest day
 * and the coldest night — "Typical: up to 29° by day, 14° at night" while no
 * forecast reaches them, "Forecast: …" once one does. Null with no weather.
 */
export function outlook(w: RaveWeather | null, tr: ReturnType<typeof useT>): string | null {
  if (!w || w.days.length === 0) return null;
  const highs = w.days.map((d) => d.highC).filter((v): v is number => v != null);
  const lows = w.days.map((d) => d.lowC).filter((v): v is number => v != null);
  if (highs.length === 0 || lows.length === 0) return null;
  const high = toDisplayTemp(Math.max(...highs), w.units);
  const low = toDisplayTemp(Math.min(...lows), w.units);
  return w.days.some((d) => d.source === 'FORECAST')
    ? tr('Forecast: up to {high}° by day, {low}° at night', { high, low })
    : tr('Typical: up to {high}° by day, {low}° at night', { high, low });
}

export function CountdownPanel({
  onClose, name, venue, gatesOpenAt, endsAt, coverUrl, logo, timezone, weatherLine,
}: {
  onClose: () => void;
  name: string;
  venue?: string;
  gatesOpenAt?: string;
  endsAt?: string;
  coverUrl?: string;
  /** The rave's own mark where there is one; the platform's otherwise. */
  logo: ReactNode;
  timezone: string;
  /** The rave's weather outlook in one line (step 65), when it has weather. */
  weatherLine?: string | null;
}) {
  const tr = useT();
  const { locale } = useLocale();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const gates = gatesOpenAt ? new Date(gatesOpenAt) : null;
  const ends = endsAt ? new Date(endsAt) : null;

  const over = !!ends && now > ends;
  const live = !!gates && !!ends && now >= gates && now <= ends;

  /*
   * THIS rave's gates, and nothing else (the user, 2026-09-28: "the counter
   * always counts to the current rave"). Each edition is its own rave with its
   * own card, so 2026's countdown rolling over to 2027 was one rave counting
   * down to another's gates. Once it is over, it says so.
   */
  const left = !over && gates ? countdown(now, gates) : null;

  const range = gates && ends
    ? localDateRange(locale, formatDateRange(gates, ends, timezone), gates, ends, timezone)
    : null;

  return (
    <div
      role="dialog"
      aria-label={tr('Countdown')}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      /*
        * A panel over the app, not a page instead of it.
        *
        * It used to be `inset: 0` — the whole viewport, chrome and all — so
        * checking how long until the gates meant losing the app entirely and
        * pressing Back to get it. It is an errand, not a destination: the
        * schedule stays behind it, the header stays reachable, and closing it
        * puts you exactly where you were because you never left.
        */
      className="countdown-scrim"
    >
      <div
        /*
          * The pilot's four stops, over THIS EDITION's artwork — the gate.
          * Light at the top so the photograph is still a photograph behind
          * the logo, heavy under the digits so tabular numerals are readable
          * in sunlight.
          */
        className="countdown-panel"
        style={{
          background:
            'linear-gradient(180deg, var(--ground-30) 0%, var(--ground-22) 16%, var(--ground-62) 44%, var(--ground-90) 100%)'
            + (coverUrl ? `, url("${coverUrl}") center / cover` : ', var(--cdf-bg)'),
        }}
      >
      <button
        onClick={onClose}
        aria-label={tr('Close the countdown')}
        className="countdown-close"
      >
        ✕
      </button>

      <Stack gap={0} className="countdown-column">
        {/* The rave's own mark, with the pilot's warm halo. */}
        <span className="countdown-logo">
          {logo}
        </span>

        <span className="countdown-tag">
          {live ? tr('Happening now') : over ? tr('This rave has ended') : tr('Gates open in')}
        </span>

        <div className="countdown-dates">
          <b className="countdown-heading">{name}</b>
          {range && <><br />{range}</>}
          {venue && <><br />{venue}</>}
        </div>
        {weatherLine && <div className="countdown-weather">{weatherLine}</div>}

        {live ? (
          <div className="countdown-statement">
            {tr('Gates are open — you are in it.')}
          </div>
        ) : left && left.totalMs > 0 ? (
          /*
            * Four columns that share the width, not four fixed tiles that
            * wrap. At 390px a 76px floor plus gaps overflowed by a hair and
            * dropped SEC onto its own line — a countdown reading 302 · 16 ·
            * 04 / 25, which is three numbers and an orphan.
            */
          <div className="countdown-grid">
            <Cell value={left.days} label={tr('days')} />
            <Cell value={left.hours} label={tr('hours')} />
            <Cell value={left.minutes} label={tr('min')} />
            <Cell value={left.seconds} label={tr('sec')} />
          </div>
        ) : (
          <div className="countdown-statement">
            {/*
              * Only when there is genuinely nothing to count to: the edition
              * is over and no successor has been published yet. Not a state
              * the app should reach often, and never while a date exists.
              */}
            {tr('That was the last one — for now.')}
          </div>
        )}

        <div className="countdown-note">
          {live ? tr('Have a good one. ✨') : tr('See you in paradise! ✨')}
        </div>
      </Stack>
      </div>
    </div>
  );
}

/**
 * The countdown's four ticking tiles alone — a rave card on the public pages
 * (the user, 2026-10-14: "show the countdowns from the app"). The same clock,
 * the same tiles; `compact` sizes them for a card. Nothing once the gates
 * are open.
 */
export function CountdownCells({ gatesOpenAt, compact }: { gatesOpenAt: string; compact?: boolean }) {
  const tr = useT();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const left = countdown(now, new Date(gatesOpenAt));
  if (left.totalMs <= 0) return null;
  return (
    <div className={compact ? 'countdown-grid is-compact' : 'countdown-grid'} role="timer" aria-label={tr('Gates open in')}>
      <Cell value={left.days} label={tr('days')} />
      <Cell value={left.hours} label={tr('hours')} />
      <Cell value={left.minutes} label={tr('min')} />
      <Cell value={left.seconds} label={tr('sec')} />
    </div>
  );
}

/** The pilot's `.cdf-cell`: a glass tile with a white-to-amber numeral. */
function Cell({ value, label }: { value: number; label: string }) {
  return (
    <div className="countdown-cell">
      <b className="countdown-digits">
        {String(value).padStart(2, '0')}
      </b>
      <span className="countdown-cell-label">
        {label}
      </span>
    </div>
  );
}
